import { Box, Tab, Card, Tabs, Stack, Avatar, useTheme, Typography } from '@mui/material';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { DashboardContent } from 'src/layouts/dashboard';
import { useTranslate } from 'src/locales';
import { useParams } from 'src/routes/hooks';
import Grid from '@mui/material/Grid2';
import { FixedCenterCircularProgress } from 'src/components/progress/fixed-center-circular-progress';
import { useGetClientQuery } from 'src/store/slices/clientsAPISlice';
import { Label } from 'src/components/label';
import { varAlpha } from 'minimal-shared/utils';
import { Iconify } from 'src/components/iconify';
import { ProfileTabView } from './ui/ProfileTab';
import { InvoicesTab } from './ui/InvoicesTab';
import { CustomersTab } from './ui/CustomersTab';
import { BalanceTab } from './ui/BalanceTab';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthContext } from 'src/auth/hooks';
import { NotAvailable } from 'src/components/not-available/NotAvailable';
import type { SyntheticEvent } from 'react';
import { useState, useCallback } from 'react';

import { useCheckFeatureFlag } from 'src/lib/hooks/useCheckFeatureFlag';

const ClientProfilePage = () => {
  const { t } = useTranslate();
  const { id } = useParams();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const shouldRenderBalancesTab = useCheckFeatureFlag('bop-should-show-balances-tab');

  const [manuallyUpdateAssets, setManuallyUpdateAssets] = useState({
    logo: '',
    banner: '',
  });

  // Get client data
  const {
    data: clientData,
    isLoading,
    isFetching,
    refetch: refetchClient,
  } = useGetClientQuery(id || '');

  // Get user
  const { user } = useAuthContext();

  // Handle change tab
  const handleChangeTab = useCallback(
    (e: SyntheticEvent, newValue: string) => {
      const params = new URLSearchParams(searchParams);
      params.set('tab', newValue);
      setSearchParams(params);

      return navigate('?tab=' + newValue);
    },
    [navigate, searchParams, setSearchParams]
  );

  return (
    <DashboardContent dataTestId="client-profile-page">
      {/* Breadcrumbs */}
      <CustomBreadcrumbs
        heading={`${clientData?.name ? clientData?.name : t('pages.titles.clientsProfile')} ${t('pages.titles.profile')}`}
        sx={{ mb: 3 }}
        links={
          clientData && [
            { name: t('pages.titles.clients'), href: '/clients' },
            { name: clientData.name },
          ]
        }
        slotProps={{
          content: {
            style: {
              gap: 0,
            },
          },
        }}
      />

      {/* Error handling */}
      {!clientData && !isLoading && !isFetching && (
        <NotAvailable
          heading={t('errors.error')}
          message={t('errors.somethingWrong')}
          titleSx={{ textTransform: 'capitalize' }}
        />
      )}

      {/* Loading indicator */}
      {(isLoading || isFetching) && <FixedCenterCircularProgress />}

      {/* Show data */}
      {!isLoading && !isFetching && clientData && (
        <>
          <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
            <Grid size={12}>
              <Card
                sx={{
                  borderRadius: theme.spacing(1),
                  overflow: 'hidden',
                }}
              >
                {/* Banner */}
                <Box
                  sx={[
                    (th) => ({
                      ...th.mixins.bgGradient({
                        images: [
                          `linear-gradient(0deg, ${varAlpha(theme.vars.palette.common.blackChannel, 0.5)}, ${varAlpha(theme.vars.palette.common.blackChannel, 0.1)})`,
                          `url(${manuallyUpdateAssets.banner || clientData.banner_uri || ''})`,
                        ],
                      }),
                      height: '240px',
                      position: 'relative',
                    }),
                  ]}
                >
                  <Stack
                    direction="row"
                    spacing={theme.spacing(3)}
                    sx={{
                      position: 'absolute',
                      left: '24px',
                      bottom: '0',
                      transform: 'translateY(29px)',
                    }}
                  >
                    <Avatar
                      alt={clientData.name}
                      src={manuallyUpdateAssets.logo || clientData.logo_uri || ''}
                      sx={{
                        mx: 'auto',
                        width: { xs: 64, md: 128 },
                        height: { xs: 64, md: 128 },
                        border: `solid 2px ${theme.palette.common.white}`,
                        fontSize: 32,
                        fontWeight: 800,
                      }}
                    />
                    <Box
                      sx={{
                        marginTop: theme.spacing(2),
                      }}
                    >
                      <Label color="primary" variant="filled">
                        {clientData.id}
                      </Label>
                      <Typography
                        variant="h4"
                        sx={{ mt: 1, color: theme.palette.common.white }}
                        fontWeight="normal"
                      >
                        {clientData.name}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Tabs */}
                <Grid
                  container
                  justifyContent="flex-end"
                  sx={{
                    padding: `0 ${theme.spacing(3)}`,
                  }}
                >
                  <Grid size="auto">
                    <Tabs value={searchParams.get('tab') || 'profile'} onChange={handleChangeTab}>
                      <Tab
                        key="profile"
                        data-test-id="client-tab-profile"
                        value="profile"
                        label={t('common.profile')}
                        icon={<Iconify icon="lucide:square-user-round" />}
                        sx={{ fontSize: 16 }}
                      />
                      <Tab
                        key="invoices"
                        data-test-id="client-tab-invoices"
                        value="invoices"
                        label={t('common.invoices')}
                        icon={<Iconify icon="lucide:receipt-text" />}
                        sx={{ fontSize: 16 }}
                      />
                      <Tab
                        key="customers"
                        data-test-id="client-tab-users"
                        value="customers"
                        label={t('sidebar.menuItems.customers')}
                        icon={<Iconify icon="lucide:users" />}
                        sx={{ fontSize: 16 }}
                      />
                      {shouldRenderBalancesTab && (
                        <Tab
                          key="balance"
                          data-test-id="client-tab-transactions"
                          value="balance"
                          label={t('common.balance')}
                          icon={<Iconify icon="lucide:arrow-right-left" />}
                          sx={{ fontSize: 16 }}
                        />
                      )}
                    </Tabs>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>

          {/* Different views */}

          {/* Profile */}
          {(searchParams.get('tab') === 'profile' || searchParams.get('tab') === null) && (
            <ProfileTabView
              clientData={clientData}
              user={user}
              onManuallyUpdateAsset={setManuallyUpdateAssets}
            />
          )}

          {/* Invoices */}
          {searchParams.get('tab') === 'invoices' && <InvoicesTab clientId={id!} key="invoices" />}

          {/* Customers */}
          {searchParams.get('tab') === 'customers' && (
            <CustomersTab clientId={id!} key="customers" />
          )}

          {/* Balance */}
          {searchParams.get('tab') === 'balance' && shouldRenderBalancesTab && (
            <BalanceTab clientData={clientData} refetchClient={refetchClient} key="balance" />
          )}
        </>
      )}
    </DashboardContent>
  );
};

export default ClientProfilePage;
